/* global debounce */

if (!customElements.get('predictive-search')) {
  // Debug logging for barcode search — mirrors the sr_logs pattern in main.js
  // (localStorage-backed, capped entry count). Needed because the iOS AppMe app
  // wraps pages in a webview with no accessible devtools/console, so failures
  // reported from inside the app can't be diagnosed without an in-page log the
  // user can retrieve themselves (surfaced via a logo-tap gesture in header.liquid).
  // Lives inside this guard (like everything else in this file) because
  // predictive-search.js loads twice on some pages (e.g. /search) — a
  // top-level const/function outside the guard throws "already declared" on
  // the second script execution.
  const BARCODE_DEBUG_LOG_KEY = 'barcode_debug_logs';
  const logBarcodeDebug = (...args) => {
    const msg = args.map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ');
    const time = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
    const entry = `[${time}] ${msg}`;
    try {
      let logs = JSON.parse(localStorage.getItem(BARCODE_DEBUG_LOG_KEY) || '[]');
      logs.push(entry);
      if (logs.length > 200) logs = logs.slice(-200);
      localStorage.setItem(BARCODE_DEBUG_LOG_KEY, JSON.stringify(logs));
    } catch (e) {}
  };
  window.getBarcodeDebugLogs = function () {
    try {
      return (JSON.parse(localStorage.getItem(BARCODE_DEBUG_LOG_KEY) || '[]')).join('\n');
    } catch (e) {
      return 'Error reading logs';
    }
  };
  window.clearBarcodeDebugLogs = function () {
    localStorage.removeItem(BARCODE_DEBUG_LOG_KEY);
    return 'Logs cleared';
  };

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.CACHE_TTL = 5 * 60 * 1000; // 5 minutes
      // Set once /search/suggest answers 417 (no predictive index for this locale, i.e. Arabic);
      // every later query then goes straight to the search-page fallback section.
      this.predictiveUnsupported = false;
      this.RECENT_SEARCHES_KEY = 'recent-searches';
      this.MAX_RECENT_SEARCHES = 5;
      this.synonymMap = null;
      this.synonymsLoaded = false;
      this.activeSynonymTerm = null;
      this.SYNONYMS_CACHE_KEY = 'search-synonyms';
      this.SYNONYMS_CACHE_TTL = 30 * 60 * 1000; // 30 min
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.skeleton = this.querySelector('.predictive-search__skeleton');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.mobileCloseBtn = this.querySelector('.js-close-mobile');
      this.loadingText = this.getAttribute('data-loading-text');

      // Image search elements
      this.imageSearchEndpoint = this.getAttribute('data-image-search-api') || 'https://kuwait-version6.vercel.app/api/image-search';

      this.addListeners();
      this.initImageSearch();
      this.initBarcodeSearch();

      // bfcache safety net: when iOS restores this page from the back/forward
      // cache, the search/image-search overlay may be frozen in its open state
      // (full-screen position:fixed) and block taps on the page underneath.
      // Force everything closed on restore. The instance + listener survive
      // bfcache, so this fires on the restored page.
      window.addEventListener('pageshow', (evt) => {
        if (evt.persisted) this.forceCloseSearchOverlays();
      });

      if (theme.settings.enableSearchSynonyms) {
        this.initSynonyms();
      }
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));

      // Mobile close button
      if (this.mobileCloseBtn) {
        this.mobileCloseBtn.addEventListener('click', () => {
          this.close();
          this.input.blur();
        });
      }
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      const query = this.getQuery();
      if (
        !query.length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      } else {
        // If synonym/transliteration is active, submit with the term that has results
        if (this.activeSynonymTerm) {
          this.input.value = this.activeSynonymTerm;
        }

        const submittedQuery = this.getQuery();
        this.saveRecentSearch(submittedQuery);

        this.trackEvent('search_submitted', {
          search_term: submittedQuery,
          search_results: this.getResultCount(),
          search_filter: this.productTypeInput?.value || null
        });
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (searchTerm.length < 2) {
        this.close();
        return;
      }

      this.showReset();
      this.getResults(searchTerm);
    }

    /**
     * Shows the clear (X) button as soon as there is a query, without waiting for results:
     * Shopify's predictive search rejects Arabic (417 "Unsupported buyer locale"), so on /ar
     * the results never open and the X would otherwise never appear.
     */
    showReset() {
      if (!this.getQuery().length) return;
      this.resetBtn.hidden = false;
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();

      // If input is empty, show recent searches
      if (!searchTerm.length) {
        this.showRecentSearches();
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) {
        // Track result click before navigation
        const item = selectedResult.closest('.predictive-search__item');
        this.trackResultClick(item);
        selectedResult.click();
      }
    }

    /**
     * Builds search parameters string for a given term.
     * @param {string} term - Search query.
     * @returns {string} URL search params string.
     */
    buildSearchParams(term) {
      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(term)}`;
      } else {
        searchParams = `q=${encodeURIComponent(term)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';
      return searchParams;
    }

    /**
     * Converts predictive-search params into search-page params for the Arabic fallback:
     * keeps the query (including any "product_type:X AND term" prefix, which the search page
     * understands too), restricts to products and asks for the fallback section only.
     * @param {string} searchParams - URL search params string built by buildSearchParams().
     * @returns {string} URL search params string for routes.search_url.
     */
    buildFallbackParams(searchParams) {
      const q = new URLSearchParams(searchParams).get('q') || '';
      // options[prefix]=last: prefix-match the last word like the suggest endpoint does, so a
      // half-typed word already returns results.
      return `q=${encodeURIComponent(q)}&type=product&options%5Bprefix%5D=last&section_id=predictive-search-fallback`;
    }

    /**
     * Fetches search results markup for given params.
     * @param {string} searchParams - URL search params string.
     * @returns {Promise<string>} Results markup.
     */
    async fetchSearchResults(searchParams) {
      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      const cached = this.cachedResults[queryKey];
      if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL) {
        return cached.markup;
      }

      let response = null;
      let resultsSelector = '#shopify-section-predictive-search';

      if (!this.predictiveUnsupported) {
        response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        // 417 "Unsupported buyer locale": Shopify has no predictive index for Arabic.
        if (response.status === 417) this.predictiveUnsupported = true;
      }

      if (this.predictiveUnsupported) {
        // Fallback: the regular search page does handle Arabic. Render its results through the
        // Section Rendering API with a section that mirrors the predictive-search markup
        // (sections/predictive-search-fallback.liquid), so everything downstream is unchanged.
        response = await fetch(`${theme.routes.searchUrl}?${this.buildFallbackParams(searchParams)}`);
        resultsSelector = '#shopify-section-predictive-search-fallback';
      }

      if (!response.ok) throw new Error(response.status);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();

      const resultsEl = tmpl.content.querySelector(resultsSelector);
      if (!resultsEl) throw new Error('predictive search: results section missing');
      const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

      this.cachedResults[queryKey] = {
        markup: resultsMarkup,
        timestamp: Date.now()
      };
      return resultsMarkup;
    }

    /**
     * Gets the results for a search term, with synonym + transliteration fallback.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();
      this.activeSynonymTerm = null;

      try {
        const params = this.buildSearchParams(searchTerm);
        const markup = await this.fetchSearchResults(params);
        const nativeCount = this.countResultsInMarkup(markup);

        // If synonym exists, merge results from all synonyms and compare with native
        if (this.synonymsLoaded) {
          const synonyms = this.lookupSynonyms(searchTerm);
          if (synonyms && synonyms.length > 0) {
            // Fetch results for all synonyms
            const synResults = [];
            for (const synonym of synonyms) {
              const synParams = this.buildSearchParams(synonym);
              const synMarkup = await this.fetchSearchResults(synParams);
              const synCount = this.countResultsInMarkup(synMarkup);
              if (synCount > 0) {
                synResults.push({ term: synonym, markup: synMarkup, count: synCount });
              }
            }

            if (synResults.length > 0) {
              // Use the synonym with the most results as the base markup
              synResults.sort((a, b) => b.count - a.count);
              const baseResult = synResults[0];
              let finalMarkup = baseResult.markup;

              // If multiple synonyms returned results, merge unique products into the base
              if (synResults.length > 1) {
                finalMarkup = this.mergeResultsMarkup(synResults.map((r) => r.markup));
              }

              const mergedCount = this.countResultsInMarkup(finalMarkup);
              const synonymLabel = synResults.map((r) => r.term).join(' / ');

              if (mergedCount > nativeCount) {
                this.activeSynonymTerm = baseResult.term;
                this.renderResults(finalMarkup);
                this.injectSynonymLabel(synonymLabel);
                this.highlightMatches(baseResult.term);
                return;
              }
            }
          }
        }

        // Native results are best (or no synonym exists) — render them
        if (nativeCount > 0) {
          this.renderResults(markup);
          return;
        }

        // No manual synonym — try auto-transliteration for Arabic terms
        if (this.isArabic(searchTerm)) {
          const candidates = this.transliterateArabic(searchTerm);

          for (const candidate of candidates) {
            const candidateParams = this.buildSearchParams(candidate);
            let candidateMarkup;
            try {
              candidateMarkup = await this.fetchSearchResults(candidateParams);
            } catch (e) {
              continue; // one failed candidate (e.g. throttled) must not close the dropdown
            }

            if (!this.hasNoResults(candidateMarkup)) {
              this.activeSynonymTerm = candidate;
              this.renderResults(candidateMarkup);
              this.injectSynonymLabel(candidate);
              this.highlightMatches(candidate);

              if (theme.settings.enableSearchSynonyms) {
                this.saveSynonymMapping(searchTerm, candidate);
              }
              return;
            }
          }

          // Transliteration failed — try AI translation (HF Space)
          const translations = await this.translateArabic(searchTerm);

          // Try top 10 AI candidates (balance between coverage and speed)
          const validTranslations = [];
          let firstMatchMarkup = null;
          let firstMatchTerm = null;

          for (const translation of translations.slice(0, 10)) {
            const transParams = this.buildSearchParams(translation);
            let transMarkup;
            try {
              transMarkup = await this.fetchSearchResults(transParams);
            } catch (e) {
              continue; // one failed candidate (e.g. throttled) must not close the dropdown
            }

            if (!this.hasNoResults(transMarkup)) {
              validTranslations.push(translation);
              if (!firstMatchMarkup) {
                firstMatchMarkup = transMarkup;
                firstMatchTerm = translation;
              }
            }
          }

          if (firstMatchTerm && firstMatchMarkup) {
            this.activeSynonymTerm = firstMatchTerm;
            this.renderResults(firstMatchMarkup);
            this.injectSynonymLabel(firstMatchTerm);
            this.highlightMatches(firstMatchTerm);

            // Save all valid translations as comma-separated synonyms (API merges them)
            if (theme.settings.enableSearchSynonyms && validTranslations.length > 0) {
              this.saveSynonymMapping(searchTerm, validTranslations.join(','));
            }
            return;
          }
        }

        // All fallbacks exhausted — show original no-results
        this.renderResults(markup);
      } catch (error) {
        this.close();
        this.showReset();
        throw error;
      }
    }

    /**
     * Shows the loading skeleton.
     */
    showSkeleton() {
      if (this.skeleton) {
        this.skeleton.hidden = false;
        this.setAttribute('open', '');
        this.overlay.classList.add('is-visible');
        document.body.classList.add('overlay-predictive-search');
      }
    }

    /**
     * Hides the loading skeleton.
     */
    hideSkeleton() {
      if (this.skeleton) {
        this.skeleton.hidden = true;
      }
    }

    /**
     * Highlights search term matches in result titles.
     * @param {string} searchTerm - The search query to highlight.
     */
    highlightMatches(searchTerm) {
      if (!searchTerm) return;

      // Extract actual search term (remove product_type filter if present)
      let term = searchTerm;
      if (term.includes(' AND ')) {
        term = term.split(' AND ').pop().trim();
      }

      const titles = this.results.querySelectorAll('.predictive-result__title');
      const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');

      titles.forEach((title) => {
        const text = title.textContent;
        if (regex.test(text)) {
          title.innerHTML = text.replace(regex, '<mark>$1</mark>');
        }
      });
    }

    /**
     * Gets recent searches from storage.
     * @returns {Array} Array of recent search terms.
     */
    getRecentSearches() {
      try {
        return theme.storageUtil.get(this.RECENT_SEARCHES_KEY, true) || [];
      } catch (e) {
        return [];
      }
    }

    /**
     * Saves a search term to recent searches.
     * @param {string} term - Search term to save.
     */
    saveRecentSearch(term) {
      if (!term || term.length < 2) return;

      try {
        let searches = this.getRecentSearches();
        // Remove if already exists (to move to top)
        searches = searches.filter((s) => s.toLowerCase() !== term.toLowerCase());
        // Add to beginning
        searches.unshift(term);
        // Limit to max
        searches = searches.slice(0, this.MAX_RECENT_SEARCHES);
        theme.storageUtil.set(this.RECENT_SEARCHES_KEY, searches);
      } catch (e) {
        // Silent fail if localStorage is blocked
      }
    }

    /**
     * Clears all recent searches.
     */
    clearRecentSearches() {
      try {
        theme.storageUtil.remove(this.RECENT_SEARCHES_KEY);
      } catch (e) {
        // Silent fail
      }
      this.close();
    }

    /**
     * Shows recent searches UI when input is empty and focused.
     */
    showRecentSearches() {
      const searches = this.getRecentSearches();
      if (!searches.length) return;

      const recentSearchesLabel = this.getAttribute('data-recent-searches-label') || 'Recent searches';
      const clearLabel = this.getAttribute('data-clear-label') || 'Clear';

      const markup = `
        <div class="recent-searches">
          <div class="recent-searches__header">
            <span class="recent-searches__title">${recentSearchesLabel}</span>
            <button class="recent-searches__clear link" type="button">${clearLabel}</button>
          </div>
          <ul class="recent-searches__list" role="list">
            ${searches.map((term) => `
              <li class="recent-searches__item">
                <a class="recent-searches__link" href="${theme.routes.searchUrl}?q=${encodeURIComponent(term)}">
                  <svg width="16" height="18" viewBox="0 0 21 23" fill="currentColor" aria-hidden="true" focusable="false" class="icon"><path d="M14.398 14.483 19 19.514l-1.186 1.014-4.59-5.017a8.317 8.317 0 0 1-4.888 1.578C3.732 17.089 0 13.369 0 8.779S3.732.472 8.336.472c4.603 0 8.335 3.72 8.335 8.307a8.265 8.265 0 0 1-2.273 5.704ZM8.336 15.53c3.74 0 6.772-3.022 6.772-6.75 0-3.729-3.031-6.75-6.772-6.75S1.563 5.051 1.563 8.78c0 3.728 3.032 6.75 6.773 6.75Z"/></svg>
                  <span>${this.escapeHtml(term)}</span>
                </a>
              </li>
            `).join('')}
          </ul>
        </div>
      `;

      this.results.innerHTML = markup;
      this.setAttribute('open', '');
      this.overlay.classList.add('is-visible');
      document.body.classList.add('overlay-predictive-search');

      // Add clear button listener
      const clearBtn = this.results.querySelector('.recent-searches__clear');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => this.clearRecentSearches());
      }

      // Add event handlers for closing
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Escapes HTML to prevent XSS.
     * @param {string} text - Text to escape.
     * @returns {string} Escaped text.
     */
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
      this.showSkeleton();
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.hideSkeleton();
      this.results.innerHTML = resultsMarkup;
      if (!this.activeSynonymTerm) {
        this.highlightMatches(this.getQuery());
      }
      this.setAttribute('results', true);
      this.open();

      // Track results shown
      const resultCount = this.getResultCount();
      this.trackEvent('search_results_shown', {
        search_term: this.getQuery(),
        result_count: resultCount
      });

      // Add click tracking to result links
      this.addResultClickTracking();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.hideSkeleton();
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }

    // ========================================
    // Synonym Methods
    // ========================================

    /**
     * Initialize synonym dictionary from API or cache.
     */
    initSynonyms() {
      try {
        const cached = sessionStorage.getItem(this.SYNONYMS_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.timestamp && (Date.now() - parsed.timestamp) < this.SYNONYMS_CACHE_TTL) {
            this.synonymMap = parsed.data;
            this.synonymsLoaded = true;
            return;
          }
        }
      } catch (e) {
        // Cache read failed
      }

      const endpoint = theme.settings.searchSynonymsEndpoint;
      const apiKey = theme.settings.searchSynonymsApiKey;
      if (!endpoint || !apiKey) return;

      fetch(`${endpoint}?apiKey=${encodeURIComponent(apiKey)}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.ok && data.synonyms) {
            this.synonymMap = data.synonyms;
            this.synonymsLoaded = true;
            try {
              sessionStorage.setItem(this.SYNONYMS_CACHE_KEY, JSON.stringify({
                data: data.synonyms,
                timestamp: Date.now()
              }));
            } catch (e) {
              // Storage full
            }
          }
        })
        .catch(() => {
          // Silent fail - synonym fallback is non-critical
        });
    }

    /**
     * Looks up synonyms for a given term.
     * @param {string} term - The search term to look up.
     * @returns {string[]|null} Array of synonym strings or null.
     */
    lookupSynonyms(term) {
      if (!this.synonymMap || !term) return null;
      const normalized = term.trim();
      return this.synonymMap[normalized] || null;
    }

    /**
     * Checks if the rendered results contain no results.
     * @param {string} markup - HTML markup string.
     * @returns {boolean} True if no results found.
     */
    hasNoResults(markup) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = markup;
      return !!tmpl.content.querySelector('.predictive-search__no-results');
    }

    /**
     * Counts the number of result items in markup (excludes "View all" button).
     * @param {string} markup - HTML markup string.
     * @returns {number} Number of result items.
     */
    countResultsInMarkup(markup) {
      if (this.hasNoResults(markup)) return 0;
      const tmpl = document.createElement('template');
      tmpl.innerHTML = markup;
      return tmpl.content.querySelectorAll('.predictive-search__item:not(.js-submit)').length;
    }

    /**
     * Merges product/collection results from multiple markup strings, deduplicating by URL.
     * Uses the first markup as the base structure and appends unique items from others.
     * @param {string[]} markups - Array of HTML markup strings.
     * @returns {string} Merged HTML markup.
     */
    mergeResultsMarkup(markups) {
      if (markups.length === 0) return '';
      if (markups.length === 1) return markups[0];

      const baseTmpl = document.createElement('template');
      baseTmpl.innerHTML = markups[0];

      // For each tab panel (products, collections), merge items
      const basePanels = baseTmpl.content.querySelectorAll('.predictive-search__tabpanel');
      basePanels.forEach((panel) => {
        const list = panel.querySelector('.predictive-search__list');
        if (!list) return;

        // Collect existing URLs to avoid duplicates
        const seenUrls = new Set();
        list.querySelectorAll('.predictive-search__item:not(.js-submit) a').forEach((a) => {
          seenUrls.add(a.getAttribute('href'));
        });

        const viewAllBtn = list.querySelector('.js-submit');
        const panelId = panel.getAttribute('id');

        // Merge items from other markups into this panel
        for (let i = 1; i < markups.length; i++) {
          const otherTmpl = document.createElement('template');
          otherTmpl.innerHTML = markups[i];
          const otherPanel = otherTmpl.content.querySelector(`#${panelId}`);
          if (!otherPanel) continue;

          otherPanel.querySelectorAll('.predictive-search__item:not(.js-submit)').forEach((item) => {
            const link = item.querySelector('a');
            const url = link?.getAttribute('href');
            if (url && !seenUrls.has(url)) {
              seenUrls.add(url);
              list.insertBefore(item.cloneNode(true), viewAllBtn);
            }
          });
        }
      });

      const wrapper = document.createElement('div');
      wrapper.appendChild(baseTmpl.content);
      return wrapper.innerHTML;
    }

    /**
     * Checks if a term contains Arabic Unicode characters.
     * @param {string} term - The search term.
     * @returns {boolean} True if term contains Arabic characters.
     */
    isArabic(term) {
      return /[\u0600-\u06FF]/.test(term);
    }

    /**
     * Transliterates Arabic text to English phonetic candidates.
     * Returns 1-3 candidate romanizations.
     * @param {string} term - Arabic search term.
     * @returns {string[]} Array of candidate romanizations.
     */
    transliterateArabic(term) {
      // Strip Arabic diacritics (harakat: fatha, damma, kasra, sukun, shadda, etc.)
      const stripped = term.replace(/[\u064B-\u065F\u0670]/g, '');

      const primaryMap = {
        '\u0627': 'a',  // ا
        '\u0623': 'a',  // أ
        '\u0625': 'i',  // إ
        '\u0622': 'a',  // آ
        '\u0628': 'b',  // ب
        '\u062A': 't',  // ت
        '\u062B': 'th', // ث
        '\u062C': 'j',  // ج
        '\u062D': 'h',  // ح
        '\u062E': 'kh', // خ
        '\u062F': 'd',  // د
        '\u0630': 'dh', // ذ
        '\u0631': 'r',  // ر
        '\u0632': 'z',  // ز
        '\u0633': 's',  // س
        '\u0634': 'sh', // ش
        '\u0635': 's',  // ص
        '\u0636': 'd',  // ض
        '\u0637': 't',  // ط
        '\u0638': 'z',  // ظ
        '\u0639': 'a',  // ع
        '\u063A': 'gh', // غ
        '\u0641': 'f',  // ف
        '\u0642': 'q',  // ق
        '\u0643': 'k',  // ك
        '\u0644': 'l',  // ل
        '\u0645': 'm',  // م
        '\u0646': 'n',  // ن
        '\u0647': 'h',  // ه
        '\u0648': 'o',  // و
        '\u064A': 'i',  // ي
        '\u0649': 'a',  // ى
        '\u0629': 'a',  // ة
        '\u0621': '',   // ء (hamza — skip)
        '\u0626': '',   // ئ (skip)
        '\u0624': '',   // ؤ (skip)
      };

      // Variant A alternates: و→u, ي→e
      const variantAMap = { '\u0648': 'u', '\u064A': 'e' };
      // Variant B alternates: ب→p
      const variantBMap = { '\u0628': 'p' };

      const transliterate = (text, overrides) => {
        let result = '';
        for (const ch of text) {
          if (overrides && ch in overrides) {
            result += overrides[ch];
          } else if (ch in primaryMap) {
            result += primaryMap[ch];
          } else {
            result += ch; // Keep non-Arabic chars (spaces, numbers, etc.)
          }
        }
        return result.trim();
      };

      const primary = transliterate(stripped, null);
      const candidates = [primary];

      // Only add Variant A if it differs from primary
      const variantA = transliterate(stripped, variantAMap);
      if (variantA !== primary) {
        candidates.push(variantA);
      }

      // Only add Variant B if it differs from all existing and term contains ب
      if (stripped.includes('\u0628')) {
        const variantB = transliterate(stripped, variantBMap);
        if (!candidates.includes(variantB)) {
          candidates.push(variantB);
        }
      }

      // Cap at 3 candidates
      return candidates.slice(0, 3);
    }

    /**
     * Saves an auto-discovered synonym mapping to the API.
     * Fire-and-forget — non-blocking, does not affect UI.
     * Also updates in-memory synonymMap for same-session instant lookups.
     * @param {string} term - Original Arabic search term.
     * @param {string} synonym - English transliteration that returned results.
     */
    /**
     * AI translation fallback for Arabic terms via the HF Space.
     * @param {string} term - Arabic search term.
     * @returns {Promise<string[]>} Translation candidates or empty array.
     */
    async translateArabic(term) {
      return this.translateViaHFSpace(term);
    }

    /**
     * HF Space translation for Arabic terms.
     * @param {string} term - Arabic search term.
     * @returns {Promise<string[]>} Translation candidates or empty array.
     */
    async translateViaHFSpace(term) {
      const spaceUrl = this.getAttribute('data-translate-api') || 'https://fayssalj-arabic-translate.hf.space';

      try {
        const callRes = await fetch(`${spaceUrl}/gradio_api/call/predict`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: [term] })
        });

        if (!callRes.ok) return [];
        const { event_id } = await callRes.json();
        if (!event_id) return [];

        const resultRes = await fetch(`${spaceUrl}/gradio_api/call/predict/${event_id}`);
        if (!resultRes.ok) return [];

        const sseText = await resultRes.text();
        const dataLine = sseText.split('\n').find((l) => l.startsWith('data: '));
        if (!dataLine) return [];

        const dataArr = JSON.parse(dataLine.slice(6));
        const jsonStr = dataArr?.[0];
        if (!jsonStr) return [];

        const parsed = JSON.parse(jsonStr);
        if (parsed.error || !parsed.translations) return [];

        return parsed.translations;
      } catch (e) {
        return [];
      }
    }

    saveSynonymMapping(term, synonym) {
      // Update in-memory map immediately for same-session lookups
      if (this.synonymMap) {
        this.synonymMap[term] = [synonym];
      }

      // Invalidate sessionStorage cache so next page load fetches updated map
      try {
        sessionStorage.removeItem(this.SYNONYMS_CACHE_KEY);
      } catch (e) {
        // Silent fail
      }

      const endpoint = theme.settings.searchSynonymsEndpoint;
      const apiKey = theme.settings.searchSynonymsApiKey;
      if (!endpoint || !apiKey) return;

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey,
          term: term.trim(),
          synonyms: synonym.trim()
        })
      }).catch(() => {
        // Silent fail — saving is best-effort
      });
    }

    /**
     * Injects a synonym label above the results.
     * @param {string} synonymTerm - The English synonym term shown to user.
     */
    injectSynonymLabel(synonymTerm) {
      if (!this.results) return;

      // Use localized label from data attribute or fallback
      const labelTemplate = this.getAttribute('data-synonym-label') || 'Results for "{{ synonym }}"';
      const labelText = labelTemplate.replace('{{ synonym }}', synonymTerm);

      const label = document.createElement('div');
      label.className = 'synonym-results-label';
      label.innerHTML = `<span class="synonym-results-label__text">${this.escapeHtml(labelText)}</span>`;

      // Prepend before the first tabpanel or results content
      const firstChild = this.results.querySelector('.predictive-search__tabpanel') || this.results.firstElementChild;
      if (firstChild) {
        firstChild.insertBefore(label, firstChild.firstChild);
      } else {
        this.results.insertBefore(label, this.results.firstChild);
      }
    }

    // ========================================
    // Analytics Methods
    // ========================================

    /**
     * Tracks an event to GTM dataLayer and dispatches a custom event.
     * @param {string} eventName - Name of the event.
     * @param {object} data - Event data.
     */
    trackEvent(eventName, data = {}) {
      // Push to GTM dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ...data,
        timestamp: new Date().toISOString()
      });

      // Dispatch custom event for theme hooks
      document.dispatchEvent(new CustomEvent(`on:search:${eventName}`, {
        bubbles: true,
        detail: data
      }));

      // Beacon to analytics API
      if (window.searchAnalytics) {
        window.searchAnalytics.track(eventName, data);
      }
    }

    /**
     * Gets the count of search results.
     * @returns {number} Result count.
     */
    getResultCount() {
      const items = this.results.querySelectorAll('.predictive-search__item:not(.js-submit)');
      return items.length;
    }

    /**
     * Adds click tracking to all result links.
     */
    addResultClickTracking() {
      const resultLinks = this.results.querySelectorAll('.predictive-search__item .js-search-link');
      resultLinks.forEach((link, index) => {
        link.addEventListener('click', (e) => {
          const item = link.closest('.predictive-search__item');
          this.trackResultClick(item, index);
        });
      });
    }

    /**
     * Tracks a result click.
     * @param {HTMLElement} item - The clicked result item.
     * @param {number} position - Position in results (optional).
     */
    trackResultClick(item, position = null) {
      if (!item) return;

      const link = item.querySelector('.js-search-link');
      const url = link?.href || '';

      // Determine result type from URL or parent
      let resultType = 'unknown';
      if (url.includes('/products/')) {
        resultType = 'product';
      } else if (url.includes('/collections/')) {
        resultType = 'collection';
      } else if (url.includes('/pages/')) {
        resultType = 'page';
      } else if (url.includes('/blogs/')) {
        resultType = 'article';
      }

      // Extract product ID from data attribute or URL
      const productId = item.dataset?.productId || this.extractIdFromUrl(url);

      // Calculate position if not provided
      if (position === null) {
        const allItems = Array.from(this.results.querySelectorAll('.predictive-search__item:not(.js-submit)'));
        position = allItems.indexOf(item);
      }

      this.trackEvent('search_result_click', {
        search_term: this.getQuery(),
        product_id: productId,
        result_type: resultType,
        position: position + 1, // 1-indexed for analytics
        result_url: url
      });
    }

    /**
     * Extracts an ID from a Shopify URL.
     * @param {string} url - URL to parse.
     * @returns {string|null} Extracted ID or null.
     */
    extractIdFromUrl(url) {
      // Try to extract handle from URL
      const match = url.match(/\/(products|collections|pages|blogs\/[^/]+\/[^/]+)\/([^/?#]+)/);
      return match ? match[2] : null;
    }

    // ========================================
    // Image Search Methods
    // ========================================

    /**
     * Initialize image search functionality.
     */
    initImageSearch() {
      this.imageSearchModal = this.querySelector('.image-search-modal');
      if (!this.imageSearchModal) return;

      this.imageSearchTrigger = this.querySelector('.js-image-search-trigger');
      this.imageSearchDropzone = this.imageSearchModal.querySelector('.js-dropzone');
      this.imageSearchFileInput = this.imageSearchModal.querySelector('.js-image-input');
      this.imageSearchPreview = this.imageSearchModal.querySelector('.js-image-preview');
      this.imageSearchPreviewContainer = this.imageSearchModal.querySelector('.image-search-modal__preview');
      this.imageSearchLoading = this.imageSearchModal.querySelector('.image-search-modal__loading');
      this.imageSearchError = this.imageSearchModal.querySelector('.image-search-modal__error');
      this.imageSearchErrorText = this.imageSearchModal.querySelector('.image-search-modal__error-text');
      this.currentImageFile = null;

      // iOS WebView: add "multiple" to trigger PHPickerViewController (photo library)
      // instead of UIImagePickerController action sheet (which includes "Take Photo" → crash)
      if (this.imageSearchFileInput && document.documentElement.classList.contains('is-ios-webview')) {
        this.imageSearchFileInput.setAttribute('multiple', '');
      }

      this.bindImageSearchEvents();
      this.initNotificationSystem();
    }

    /**
     * Show error message inside modal.
     * @param {string} message - Error message to display.
     */
    showModalError(message) {
      if (!this.imageSearchError || !this.imageSearchErrorText) return;

      this.imageSearchErrorText.textContent = message;
      this.imageSearchError.hidden = false;

      // Auto-hide after 5 seconds
      setTimeout(() => {
        if (this.imageSearchError) {
          this.imageSearchError.hidden = true;
        }
      }, 5000);
    }

    /**
     * Hide error message in modal.
     */
    hideModalError() {
      if (this.imageSearchError) {
        this.imageSearchError.hidden = true;
      }
    }

    /**
     * Initialize custom notification system.
     */
    initNotificationSystem() {
      this.notificationEl = document.getElementById('image-search-notification');
      this.notificationTimeout = null;

      // Close button handler
      if (this.notificationEl) {
        const closeBtn = this.notificationEl.querySelector('.image-search-notification__close');
        closeBtn?.addEventListener('click', () => this.hideNotification());
      }
    }

    /**
     * Show custom notification with brand styling.
     * @param {string} message - Message to display.
     * @param {string} type - Type: 'info', 'error', 'success'.
     * @param {number} duration - Duration in ms (default 4000).
     */
    showNotification(message, type = 'info', duration = 4000) {
      if (!this.notificationEl) return;

      // Clear existing timeout
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
      }

      // Set content and type
      const contentEl = this.notificationEl.querySelector('.image-search-notification__content');
      if (contentEl) {
        contentEl.textContent = message;
      }

      // Update icon based on type
      const iconEl = this.notificationEl.querySelector('.image-search-notification__icon');
      const icons = {
        info: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
        error: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
        success: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
      };
      if (iconEl) {
        iconEl.innerHTML = icons[type] || icons.info;
      }

      // Reset classes and add type class
      this.notificationEl.className = 'image-search-notification';
      if (type === 'error') {
        this.notificationEl.classList.add('image-search-notification--error');
      } else if (type === 'success') {
        this.notificationEl.classList.add('image-search-notification--success');
      }

      // Show notification
      this.notificationEl.hidden = false;
      requestAnimationFrame(() => {
        this.notificationEl.classList.add('is-visible');
      });

      // Auto-hide after duration
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification();
      }, duration);
    }

    /**
     * Hide notification.
     */
    hideNotification() {
      if (!this.notificationEl) return;

      this.notificationEl.classList.remove('is-visible');
      setTimeout(() => {
        this.notificationEl.hidden = true;
      }, 400);

      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
      }
    }

    /**
     * Bind image search event handlers.
     */
    bindImageSearchEvents() {
      // Trigger button - open modal
      this.imageSearchTrigger?.addEventListener('click', () => this.openImageSearchModal());

      // Close buttons
      this.imageSearchModal?.querySelectorAll('.js-image-search-close')
        .forEach((btn) => btn.addEventListener('click', () => this.closeImageSearchModal()));

      // File input change with error handling for WebViews
      this.imageSearchFileInput?.addEventListener('change', (e) => {
        try {
          if (e.target.files?.[0]) {
            this.hideModalError(); // Clear previous errors
            this.handleImageFile(e.target.files[0]);
          }
        } catch (err) {
          console.error('Error handling image file:', err);
          this.showModalError(
            this.getAttribute('data-image-error') || 'Unable to process image. Please try again.'
          );
        }
      });

      // Drag and drop
      if (this.imageSearchDropzone) {
        this.imageSearchDropzone.addEventListener('dragover', (e) => {
          e.preventDefault();
          this.imageSearchDropzone.classList.add('is-dragover');
        });

        this.imageSearchDropzone.addEventListener('dragleave', () => {
          this.imageSearchDropzone.classList.remove('is-dragover');
        });

        this.imageSearchDropzone.addEventListener('drop', (e) => {
          e.preventDefault();
          this.imageSearchDropzone.classList.remove('is-dragover');
          if (e.dataTransfer.files?.[0]) {
            this.handleImageFile(e.dataTransfer.files[0]);
          }
        });
      }

      // Browse button - opens file picker
      this.imageSearchModal?.querySelector('.js-browse-files')
        ?.addEventListener('click', () => {
          try {
            this.imageSearchFileInput?.click();
          } catch (err) {
            console.error('Error opening file picker:', err);
          }
        });

      // Search button
      this.imageSearchModal?.querySelector('.js-search-image')
        ?.addEventListener('click', () => this.performImageSearch());

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.imageSearchModal && !this.imageSearchModal.hidden) {
          this.closeImageSearchModal();
        }
      });
    }

    /**
     * Open image search modal.
     */
    openImageSearchModal() {
      if (!this.imageSearchModal) return;
      this.imageSearchModal.hidden = false;
      document.body.classList.add('overflow-hidden');
      this.resetImageSearchModal();
    }

    /**
     * Close image search modal.
     */
    closeImageSearchModal() {
      if (!this.imageSearchModal) return;
      this.imageSearchModal.hidden = true;
      document.body.classList.remove('overflow-hidden');
    }

    /**
     * Tear down every search overlay and its body scroll-lock classes.
     * Used before navigating away (so the bfcache snapshot is already closed)
     * and on bfcache restore (so a restored page isn't left with a full-screen
     * overlay blocking taps).
     */
    forceCloseSearchOverlays() {
      try { this.close(); } catch (e) {}
      try { this.closeImageSearchModal(); } catch (e) {}
      try { this.closeBarcodeSearchModal(); } catch (e) {}
      document.body.classList.remove('overflow-hidden', 'overlay-predictive-search');
    }

    /**
     * Reset image search modal to initial state.
     */
    resetImageSearchModal() {
      this.currentImageFile = null;
      if (this.imageSearchDropzone) this.imageSearchDropzone.hidden = false;
      if (this.imageSearchPreviewContainer) this.imageSearchPreviewContainer.hidden = true;
      if (this.imageSearchLoading) this.imageSearchLoading.hidden = true;
      if (this.imageSearchFileInput) this.imageSearchFileInput.value = '';

      // Reset sparkle effects
      const searchButton = this.imageSearchModal?.querySelector('.js-search-image');
      const sparkles = this.imageSearchModal?.querySelector('.js-sparkles');
      const analyzingText = this.imageSearchModal?.querySelector('.js-analyzing-text');

      if (searchButton) searchButton.hidden = false;
      if (sparkles) sparkles.hidden = true;
      if (analyzingText) analyzingText.hidden = true;

      this.hideModalError(); // Clear any errors
    }

    /**
     * Handle uploaded/dropped image file.
     * @param {File} file - The image file.
     */
    handleImageFile(file) {
      if (!file || !file.type.startsWith('image/')) {
        this.showModalError('Please select a valid image file');
        return;
      }

      // Validate file size (max 10MB - compressed client-side before upload)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        this.showModalError(`Image too large (${fileSizeMB}MB). Maximum size is 10MB.`);
        return;
      }

      this.currentImageFile = file;
      this.hideModalError(); // Clear any previous errors

      const reader = new FileReader();
      reader.onload = (e) => {
        if (this.imageSearchPreview) {
          this.imageSearchPreview.src = e.target.result;
        }
        if (this.imageSearchDropzone) this.imageSearchDropzone.hidden = true;
        if (this.imageSearchPreviewContainer) this.imageSearchPreviewContainer.hidden = false;
      };
      reader.readAsDataURL(file);
    }

    /**
     * Perform image search using the uploaded image.
     */
    async performImageSearch() {
      if (!this.currentImageFile) return;

      // Track image search usage
      this.trackEvent('image_search_used', {
        file_type: this.currentImageFile.type,
        file_size: this.currentImageFile.size
      });

      // Show loading state with sparkle effects
      const searchButton = this.imageSearchModal.querySelector('.js-search-image');
      const sparkles = this.imageSearchModal.querySelector('.js-sparkles');
      const analyzingText = this.imageSearchModal.querySelector('.js-analyzing-text');

      if (searchButton) searchButton.hidden = true;
      if (sparkles) sparkles.hidden = false;
      if (analyzingText) analyzingText.hidden = false;

      try {
        // Compress client-side (max 1024px JPEG) so even large mobile photos stay
        // well under Vercel's 4.5MB body limit, then send the bytes straight to the
        // backend. No third-party image host — the backend stores the thumbnail.
        const image = await this.compressImage(this.currentImageFile);

        const response = await fetch(this.imageSearchEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ image })
        });

        const data = await response.json();

        // Check for API error response
        if (!response.ok || data.error) {
          throw new Error(data.error || `Image search failed: ${response.status}`);
        }
        console.log(`[Image Search API] Backend returned ${data.products?.length || 0} products`);

        if (data.products && data.products.length > 0) {
          // Close modal and navigate to search results
          this.closeImageSearchModal();
          this.displayImageSearchResults(data.products);
        } else {
          // No results found
          this.showModalError(
            this.getAttribute('data-image-no-results') || 'No similar products found'
          );
          this.resetImageSearchModal();
        }
      } catch (error) {
        console.error('Image search error:', error);
        // Show actual error message if available, otherwise generic fallback
        const errorMessage = error.message && !error.message.includes('fetch')
          ? error.message
          : (this.getAttribute('data-image-error') || 'Could not process image. Please try again.');
        this.showModalError(errorMessage);
        this.resetImageSearchModal();
      }
    }

    /**
     * Convert file to base64 string.
     * @param {File} file - The file to convert.
     * @returns {Promise<string>} Base64 encoded string.
     */
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Extract base64 data (remove data:image/xxx;base64, prefix)
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }

    /**
     * Downscale an image to a max dimension and re-encode as JPEG.
     * @param {File} file - The image file.
     * @param {number} maxDim - Max width/height in px.
     * @param {number} quality - JPEG quality 0-1.
     * @returns {Promise<string>} Raw base64 (no data: prefix).
     */
    compressImage(file, maxDim = 1024, quality = 0.8) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            if (width >= height) { height = Math.round(height * maxDim / width); width = maxDim; }
            else { width = Math.round(width * maxDim / height); height = maxDim; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          try {
            resolve(canvas.toDataURL('image/jpeg', quality).split(',')[1]);
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
        img.src = url;
      });
    }

    /**
     * Display image search results - navigate to results page.
     * @param {Array} productIdentifiers - Array of product IDs or handles from API.
     */
    displayImageSearchResults(productIdentifiers) {
      // API returns handles for cross-store compatibility
      const handles = productIdentifiers.slice(0, 12);

      // Track successful image search
      this.trackEvent('image_search_results', {
        result_count: handles.length,
        handles: handles.slice(0, 5).join(',')
      });

      // Store handles in sessionStorage
      try {
        sessionStorage.setItem('image_search_handles', JSON.stringify(handles));
        // Clear old cached results to force re-render on new search (both languages)
        sessionStorage.removeItem('image_search_results_html_en');
        sessionStorage.removeItem('image_search_results_status_en');
        sessionStorage.removeItem('image_search_results_html_ar');
        sessionStorage.removeItem('image_search_results_status_ar');
      } catch (e) {
        console.error('Failed to store image search results:', e);
      }

      // Close the search drawer + image-search modal and drop the body scroll
      // locks BEFORE navigating, so the homepage snapshot iOS freezes into the
      // bfcache is already closed (otherwise the restored page keeps a
      // full-screen overlay over the product grid and nothing is clickable).
      this.forceCloseSearchOverlays();

      // Navigate to image search results page (with locale prefix if Arabic)
      const isArabic = window.location.pathname.startsWith('/ar');
      window.location.href = isArabic ? '/ar/pages/image-search-results' : '/pages/image-search-results';
    }

    /**
     * Barcode search — same modal behaviour as image search (open,
     * dropzone/preview, close button), but on a match it redirects straight
     * to the product page instead of a results grid, since a barcode has
     * exactly one correct product.
     */
    initBarcodeSearch() {
      this.barcodeSearchModal = this.querySelector('.barcode-search-modal');
      if (!this.barcodeSearchModal) return;

      this.barcodeSearchEndpoint = this.barcodeSearchModal.dataset.barcodeSearchApi
        || 'https://kuwait-version6.vercel.app/api/barcode-search';

      this.barcodeSearchTrigger = this.querySelector('.js-barcode-search-trigger');
      this.barcodeSearchDropzone = this.barcodeSearchModal.querySelector('.js-dropzone');
      this.barcodeSearchFileInput = this.barcodeSearchModal.querySelector('.js-barcode-input');
      this.barcodeSearchPreview = this.barcodeSearchModal.querySelector('.js-barcode-preview');
      this.barcodeSearchPreviewContainer = this.barcodeSearchModal.querySelector('.image-search-modal__preview');
      this.barcodeSearchLoading = this.barcodeSearchModal.querySelector('.image-search-modal__loading');
      this.barcodeSearchError = this.barcodeSearchModal.querySelector('.image-search-modal__error');
      this.barcodeSearchErrorText = this.barcodeSearchModal.querySelector('.image-search-modal__error-text');
      this.barcodeSearchResult = this.barcodeSearchModal.querySelector('.image-search-modal__result');
      this.barcodeSearchResultText = this.barcodeSearchModal.querySelector('.js-barcode-result-text');
      this.barcodeSearchResultLink = this.barcodeSearchModal.querySelector('.js-barcode-result-link');
      this.barcodeSearchResultProduct = this.barcodeSearchModal.querySelector('.js-barcode-result-product');
      this.currentBarcodeFile = null;

      // iOS WebView: same PHPickerViewController workaround as image search.
      if (this.barcodeSearchFileInput && document.documentElement.classList.contains('is-ios-webview')) {
        this.barcodeSearchFileInput.setAttribute('multiple', '');
      }

      this.bindBarcodeSearchEvents();
    }

    showBarcodeModalError(message) {
      if (!this.barcodeSearchError || !this.barcodeSearchErrorText) return;
      this.barcodeSearchErrorText.textContent = message;
      this.barcodeSearchError.hidden = false;
      setTimeout(() => {
        if (this.barcodeSearchError) this.barcodeSearchError.hidden = true;
      }, 5000);
    }

    hideBarcodeModalError() {
      if (this.barcodeSearchError) this.barcodeSearchError.hidden = true;
    }

    // Shows a real tappable <a href> in the modal instead of auto-redirecting.
    // See the comment above .image-search-modal__result in
    // predictive-search.liquid for why: some webview app wrappers block
    // programmatic navigation, but a genuine user tap on a real link always
    // works, so this sidesteps the problem entirely rather than fighting it.
    // `productCardHtml` (optional) is the real product-card-mini markup
    // fetched via fetchBarcodeProductCard() — shown above the link when
    // available (only applies to a direct match; the text-search fallback
    // has no single product to show).
    showBarcodeResult(text, href, linkLabel, productCardHtml) {
      if (!this.barcodeSearchResult || !this.barcodeSearchResultLink) return;
      if (this.barcodeSearchPreviewContainer) this.barcodeSearchPreviewContainer.hidden = true;
      if (this.barcodeSearchDropzone) this.barcodeSearchDropzone.hidden = true;
      if (this.barcodeSearchLoading) this.barcodeSearchLoading.hidden = true;
      if (this.barcodeSearchResultText) this.barcodeSearchResultText.textContent = text;
      this.barcodeSearchResultLink.href = href;
      this.barcodeSearchResultLink.textContent = linkLabel;

      if (productCardHtml && this.barcodeSearchResultProduct) {
        this.barcodeSearchResultProduct.innerHTML = productCardHtml;
        this.barcodeSearchResultProduct.hidden = false;
      } else if (this.barcodeSearchResultProduct) {
        this.barcodeSearchResultProduct.hidden = true;
      }

      this.barcodeSearchResult.hidden = false;
    }

    // Fetches the matched product's real product-card-mini markup via the
    // Section Rendering API (see sections/barcode-result-card.liquid) —
    // reuses the theme's actual card component (correct money formatting,
    // sale pricing, RTL, images) instead of hand-building an equivalent.
    // Best-effort: returns null on any failure so the caller can still show
    // the plain text+link result without a thumbnail.
    async fetchBarcodeProductCard(handle, isArabic) {
      try {
        const path = isArabic ? `/ar/products/${handle}` : `/products/${handle}`;
        const res = await fetch(`${path}?section_id=barcode-result-card`);
        if (!res.ok) return null;
        const html = (await res.text()).trim();
        return html || null;
      } catch (e) {
        logBarcodeDebug('fetchBarcodeProductCard failed', e.message);
        return null;
      }
    }

    bindBarcodeSearchEvents() {
      this.barcodeSearchTrigger?.addEventListener('click', () => this.openBarcodeSearchModal());

      this.barcodeSearchModal?.querySelectorAll('.js-barcode-search-close')
        .forEach((btn) => btn.addEventListener('click', () => this.closeBarcodeSearchModal()));

      this.barcodeSearchFileInput?.addEventListener('change', (e) => {
        try {
          if (e.target.files?.[0]) {
            this.hideBarcodeModalError();
            this.handleBarcodeFile(e.target.files[0]);
          }
        } catch (err) {
          console.error('Error handling barcode image file:', err);
          this.showBarcodeModalError(
            this.barcodeSearchModal.dataset.barcodeError || 'Unable to process image. Please try again.'
          );
        }
      });

      if (this.barcodeSearchDropzone) {
        this.barcodeSearchDropzone.addEventListener('dragover', (e) => {
          e.preventDefault();
          this.barcodeSearchDropzone.classList.add('is-dragover');
        });

        this.barcodeSearchDropzone.addEventListener('dragleave', () => {
          this.barcodeSearchDropzone.classList.remove('is-dragover');
        });

        this.barcodeSearchDropzone.addEventListener('drop', (e) => {
          e.preventDefault();
          this.barcodeSearchDropzone.classList.remove('is-dragover');
          if (e.dataTransfer.files?.[0]) {
            this.handleBarcodeFile(e.dataTransfer.files[0]);
          }
        });
      }

      this.barcodeSearchModal?.querySelector('.js-browse-files')
        ?.addEventListener('click', () => {
          try {
            this.barcodeSearchFileInput?.click();
          } catch (err) {
            console.error('Error opening file picker:', err);
          }
        });

      this.barcodeSearchModal?.querySelector('.js-search-barcode')
        ?.addEventListener('click', () => this.performBarcodeSearch());

      // Lets the user swap the photo without closing/reopening the modal.
      this.barcodeSearchModal?.querySelector('.js-change-barcode')
        ?.addEventListener('click', () => {
          this.resetBarcodeSearchModal();
          this.barcodeSearchFileInput?.click();
        });

      this.barcodeSearchModal?.querySelector('.js-barcode-result-again')
        ?.addEventListener('click', () => {
          this.resetBarcodeSearchModal();
          this.barcodeSearchFileInput?.click();
        });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.barcodeSearchModal && !this.barcodeSearchModal.hidden) {
          this.closeBarcodeSearchModal();
        }
      });
    }

    openBarcodeSearchModal() {
      if (!this.barcodeSearchModal) return;
      this.barcodeSearchModal.hidden = false;
      document.body.classList.add('overflow-hidden');
      this.resetBarcodeSearchModal();
    }

    closeBarcodeSearchModal() {
      if (!this.barcodeSearchModal) return;
      this.barcodeSearchModal.hidden = true;
      document.body.classList.remove('overflow-hidden');
    }

    resetBarcodeSearchModal() {
      this.currentBarcodeFile = null;
      if (this.barcodeSearchDropzone) this.barcodeSearchDropzone.hidden = false;
      if (this.barcodeSearchPreviewContainer) this.barcodeSearchPreviewContainer.hidden = true;
      if (this.barcodeSearchLoading) this.barcodeSearchLoading.hidden = true;
      if (this.barcodeSearchResult) this.barcodeSearchResult.hidden = true;
      if (this.barcodeSearchResultProduct) this.barcodeSearchResultProduct.hidden = true;
      if (this.barcodeSearchFileInput) this.barcodeSearchFileInput.value = '';

      const searchButton = this.barcodeSearchModal?.querySelector('.js-search-barcode');
      const sparkles = this.barcodeSearchModal?.querySelector('.js-sparkles');
      const analyzingText = this.barcodeSearchModal?.querySelector('.js-analyzing-text');

      if (searchButton) searchButton.hidden = false;
      if (sparkles) sparkles.hidden = true;
      if (analyzingText) analyzingText.hidden = true;

      this.hideBarcodeModalError();
    }

    handleBarcodeFile(file) {
      if (!file || !file.type.startsWith('image/')) {
        this.showBarcodeModalError('Please select a valid image file');
        return;
      }

      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        this.showBarcodeModalError(`Image too large (${fileSizeMB}MB). Maximum size is 10MB.`);
        return;
      }

      this.currentBarcodeFile = file;
      this.hideBarcodeModalError();

      const reader = new FileReader();
      reader.onload = (e) => {
        if (this.barcodeSearchPreview) {
          this.barcodeSearchPreview.src = e.target.result;
        }
        if (this.barcodeSearchDropzone) this.barcodeSearchDropzone.hidden = true;
        if (this.barcodeSearchPreviewContainer) this.barcodeSearchPreviewContainer.hidden = false;
      };
      reader.readAsDataURL(file);
    }

    /**
     * Perform barcode search using the uploaded image.
     */
    async performBarcodeSearch() {
      if (!this.currentBarcodeFile) return;

      this.trackEvent('barcode_search_used', {
        file_type: this.currentBarcodeFile.type,
        file_size: this.currentBarcodeFile.size
      });
      logBarcodeDebug('start', 'file_type=' + this.currentBarcodeFile.type, 'file_size=' + this.currentBarcodeFile.size, 'ua=' + navigator.userAgent);

      const searchButton = this.barcodeSearchModal.querySelector('.js-search-barcode');
      const sparkles = this.barcodeSearchModal.querySelector('.js-sparkles');
      const analyzingText = this.barcodeSearchModal.querySelector('.js-analyzing-text');

      if (searchButton) searchButton.hidden = true;
      if (sparkles) sparkles.hidden = false;
      if (analyzingText) analyzingText.hidden = false;

      try {
        // Higher-res than image-search's 1024px default — a barcode needs
        // enough resolution for the detector to find fine bar detail;
        // similarity search only needs enough for gestalt shape/color.
        const image = await this.compressImage(this.currentBarcodeFile, 1600, 0.85);
        logBarcodeDebug('compressed image, base64 length=' + image.length);

        logBarcodeDebug('fetching', this.barcodeSearchEndpoint);
        const response = await fetch(this.barcodeSearchEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image })
        });
        logBarcodeDebug('response received, status=' + response.status, 'ok=' + response.ok);

        const data = await response.json();
        logBarcodeDebug('response body', data);

        if (!response.ok || data.error) {
          throw new Error(data.error || `Barcode search failed: ${response.status}`);
        }

        const isWebview = document.documentElement.classList.contains('is-webview');

        if (data.found && data.handle) {
          this.trackEvent('barcode_search_match', { handle: data.handle });
          const isArabic = window.location.pathname.startsWith('/ar');
          const targetUrl = isArabic ? `/ar/products/${data.handle}` : `/products/${data.handle}`;

          if (isWebview) {
            // Programmatic navigation is blocked in some webview app wrappers
            // (confirmed: AppMe on iOS) — show a real tappable link + product
            // preview instead of fighting it. Elsewhere the direct redirect
            // below already works fine and is the smoother experience.
            logBarcodeDebug('matched (webview), showing tap-to-continue link to', targetUrl);
            const productCardHtml = await this.fetchBarcodeProductCard(data.handle, isArabic);
            this.showBarcodeResult(
              this.barcodeSearchModal.dataset.barcodeFoundText || 'Product found!',
              targetUrl,
              this.barcodeSearchModal.dataset.barcodeViewProduct || 'View Product',
              productCardHtml
            );
          } else {
            logBarcodeDebug('matched, redirecting to product', targetUrl);
            this.forceCloseSearchOverlays();
            window.location.href = targetUrl;
          }
        } else if (data.search_term) {
          // Decoded the barcode, but it didn't match on our first lookup —
          // the printed code on a label isn't always in the field we check
          // (e.g. it can be the SKU rather than the barcode). Rather than a
          // dead end, hand the decoded value to Shopify's own search (which
          // already indexes SKUs, see predictive_search_include_skus) so the
          // customer still gets a useful result.
          this.trackEvent('barcode_search_fallback_to_text', { term: data.search_term });
          const isArabic = window.location.pathname.startsWith('/ar');
          const searchPath = isArabic ? '/ar/search' : '/search';
          const targetUrl = `${searchPath}?q=${encodeURIComponent(data.search_term)}`;

          if (isWebview) {
            logBarcodeDebug('no direct match (webview), showing tap-to-continue link to', targetUrl);
            this.showBarcodeResult(
              this.barcodeSearchModal.dataset.barcodeNoDirectMatch || 'No exact match found — see search results',
              targetUrl,
              this.barcodeSearchModal.dataset.barcodeViewResults || 'View Results'
            );
          } else {
            logBarcodeDebug('no direct match, redirecting to text search', targetUrl);
            this.forceCloseSearchOverlays();
            window.location.href = targetUrl;
          }
        } else {
          logBarcodeDebug('no barcode decoded, showing not-recognized error');
          this.showBarcodeModalError(
            this.barcodeSearchModal.dataset.barcodeNoResults || 'Barcode not recognized'
          );
          this.resetBarcodeSearchModal();
        }
      } catch (error) {
        logBarcodeDebug('CAUGHT ERROR', 'name=' + error.name, 'message=' + error.message, 'stack=' + (error.stack || '').slice(0, 500));
        console.error('Barcode search error:', error);
        const errorMessage = error.message && !error.message.includes('fetch')
          ? error.message
          : (this.barcodeSearchModal.dataset.barcodeError || 'Could not process image. Please try again.');
        this.showBarcodeModalError(errorMessage);
        this.resetBarcodeSearchModal();
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
